import { useState } from 'react';
import type { Mood } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Droplets, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WaterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (reflection: string, nextStep?: string, mood?: Mood) => Promise<boolean>;
  ideaTitle?: string;
  isTree?: boolean;
}

const moods: { value: Mood; label: string; emoji: string }[] = [
  { value: 'curious', label: 'Curious', emoji: '🤔' },
  { value: 'excited', label: 'Excited', emoji: '✨' },
  { value: 'uncertain', label: 'Uncertain', emoji: '😕' },
  { value: 'heavy', label: 'Heavy', emoji: '😮‍💨' },
  { value: 'neutral', label: 'Neutral', emoji: '😐' },
];

export function WaterSheet({ open, onOpenChange, onSubmit, ideaTitle, isTree = false }: WaterSheetProps) {
  const [reflection, setReflection] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [selectedMood, setSelectedMood] = useState<Mood | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reflection.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const success = await onSubmit(
        reflection.trim(),
        nextStep.trim() || undefined,
        selectedMood
      );
      
      if (success) {
        setReflection('');
        setNextStep('');
        setSelectedMood(undefined);
        onOpenChange(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{isTree ? 'Deepen Your Understanding' : 'Water Your Idea'}</SheetTitle>
          <SheetDescription>
            {ideaTitle ? `Reflect on "${ideaTitle}"` : (isTree ? 'What deepened your understanding?' : 'What has changed since last time?')}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Reflection */}
          <div className="space-y-2">
            <Label htmlFor="reflection">
              {isTree ? 'What deepened your understanding?' : 'What changed since last time?'}
            </Label>
            <Textarea
              id="reflection"
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="Describe your reflection..."
              className="resize-none"
              rows={4}
            />
          </div>

          {/* Next Step (optional) */}
          <div className="space-y-2">
            <Label htmlFor="nextStep">
              What's the smallest next step? <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="nextStep"
              value={nextStep}
              onChange={(e) => setNextStep(e.target.value)}
              placeholder="One small action you could take..."
              className="resize-none"
              rows={2}
            />
          </div>

          {/* Mood Selector */}
          <div className="space-y-2">
            <Label>How are you feeling about this idea?</Label>
            <div className="flex flex-wrap gap-2">
              {moods.map((mood) => (
                <Button
                  key={mood.value}
                  type="button"
                  variant={selectedMood === mood.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedMood(
                    selectedMood === mood.value ? undefined : mood.value
                  )}
                  className={cn(
                    'rounded-full',
                    selectedMood === mood.value && 'bg-primary'
                  )}
                >
                  {mood.emoji} {mood.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={!reflection.trim() || isSubmitting}
            className="w-full"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Watering...
              </>
            ) : (
              <>
                <Droplets className="mr-2 h-4 w-4" />
                Water Seed
              </>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
